/**
 * Eine Neuerung als Eintrag: kurze fette Überschrift, ein Satz, „Wo: …“ und — wenn es eins gibt — ein kleines Bild.
 * (17.10.2026, Martin 08:44: „Was ist neu“ schöner, mit Screenshots, damit man weiß, wonach man schauen soll.)
 *
 * Gemeinsam für die Karte „Das ist neu“ (lib/neu-chip.ts) und das Fenster „Was ist neu“ (lib/was-ist-neu.ts).
 * Zerlegt hat der Server (shared/neu-punkte.ts); hier wird nur gezeichnet — alles als textContent, nie innerHTML.
 *
 *   - Das Bild steht rechts als Vorschau (lazy geladen). Antippen macht es groß (`data-gross` am Eintrag), noch
 *     einmal antippen wieder klein. Ein Knopf mit Namen, damit Vorleser und Tastatur es erreichen.
 *   - Lädt das Bild nicht, verschwindet die Vorschau: der Eintrag sieht dann aus wie einer ohne Bild.
 *   - Nur Adressen der Form `/neu/<name>` (istBildUrl) — alles andere wird gar nicht erst gezeichnet.
 * Aussehen: style.css (.neup*).
 */

import { istBildUrl } from "../../shared/neu-punkte.js";

/** Eine Neuerung, wie sie die App vom Server bekommt. */
export interface NeuPunktAnzeige {
  titel: string;
  text: string;
  wo: string;
  bildUrl?: string;
}

/** Streng lesen, was der Server schickt: unbekannte Felder fallen weg, eine fremde Bildadresse auch. */
export function punktAus(roh: unknown): NeuPunktAnzeige | null {
  const o = roh as Partial<Record<keyof NeuPunktAnzeige, unknown>> | null;
  if (!o || typeof o !== "object") return null;
  const text = typeof o.text === "string" ? o.text.trim() : "";
  if (!text) return null;
  const p: NeuPunktAnzeige = {
    titel: typeof o.titel === "string" ? o.titel.trim() : "",
    text,
    wo: typeof o.wo === "string" ? o.wo.trim() : "",
  };
  if (typeof o.bildUrl === "string" && istBildUrl(o.bildUrl)) p.bildUrl = o.bildUrl;
  return p;
}

/** Die Neuerungen eines Eintrags; ein Dienst ohne Zerlegung liefert nur `text` — dann ist das EINE Neuerung. */
export function punkteVon(e: { text: string; punkte?: NeuPunktAnzeige[] }): NeuPunktAnzeige[] {
  return e.punkte && e.punkte.length > 0 ? e.punkte : [{ titel: "", text: e.text, wo: "" }];
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Wie das Bild für Vorleser heißt: was zu sehen ist, nicht der Dateiname. */
export function bildName(p: NeuPunktAnzeige): string {
  return `Bildschirmfoto: ${p.titel || p.text.replace(/[.!?…]$/u, "")}`;
}

/**
 * Ein Eintrag. `klasse` sind die Klassen des Rahmens (Karte: „neuk-satz“, Fenster: „wasneu-punkt“);
 * Kopfzeile, Text, Ort und Bild sind überall gleich gebaut.
 */
export function bauePunkt(p: NeuPunktAnzeige, tag: "div" | "li", klasse: string): HTMLElement {
  const rahmen = el(tag, `${klasse} neup`);
  const inhalt = el("div", "neup-inhalt");
  if (p.titel) inhalt.appendChild(el("strong", "neup-titel", p.titel));
  inhalt.appendChild(el("p", "neup-text", p.text));
  if (p.wo) {
    const wo = el("p", "neup-wo");
    wo.append(el("span", "neup-wo-marke", "Wo"), el("span", "neup-wo-ort", p.wo));
    inhalt.appendChild(wo);
  }
  rahmen.appendChild(inhalt);

  if (p.bildUrl && istBildUrl(p.bildUrl)) {
    const knopf = el("button", "neup-bild");
    knopf.type = "button";
    knopf.setAttribute("aria-expanded", "false");
    knopf.setAttribute("aria-label", `${bildName(p)} – vergrößern`);
    const bild = document.createElement("img");
    bild.className = "neup-img";
    bild.loading = "lazy";
    bild.decoding = "async";
    bild.alt = bildName(p);
    bild.src = p.bildUrl;
    // Ohne Bild sieht der Eintrag aus wie einer, der nie eins hatte.
    bild.addEventListener("error", () => {
      knopf.remove();
      rahmen.removeAttribute("data-bild");
      rahmen.removeAttribute("data-gross");
    });
    knopf.appendChild(bild);
    knopf.addEventListener("click", () => {
      const gross = !rahmen.hasAttribute("data-gross");
      rahmen.toggleAttribute("data-gross", gross);
      knopf.setAttribute("aria-expanded", gross ? "true" : "false");
      knopf.setAttribute("aria-label", `${bildName(p)} – ${gross ? "verkleinern" : "vergrößern"}`);
    });
    rahmen.setAttribute("data-bild", "");
    rahmen.appendChild(knopf);
  }
  return rahmen;
}
